// рефакторинг в https://jira.cian.tech/browse/COMM-920
/* eslint-disable max-lines */
import { v4 as uuidv4 } from 'uuid';
import { assocPath, compose } from 'ramda';

import { getClientDeviceType, getAnalyticsSiteType } from '../../../../../../shared/utils';
import { selectIsNewOffer } from '../../../../../../shared/selectors/offer/selectIsNewOffer';

import { trackClickIsAreaParts } from '../commercial-multi-area/tracking';
import cianPhotoTransfer from '@cian/ui/app/components/photo-transfer/index.jsm';

import template from './commercial-area-parts.jade';

import { findMax, getValidationMessage } from './utils';
import { ValidationHelpers } from './validation-helpers';

import { trackSubmitAreaPartsOffer } from './tracking';

const MAX_AREA_PARTS_LENGTH = 30;

import { VALIDATABLE_FIELDS } from '../../../../../../shared/components/AreaParts/constants';

export default angular
  .module('cian.addForm.commercialAreaParts', [cianPhotoTransfer])
  .directive('cianAfAboutObjectCommercialAreaParts', () => ({
    scope: {},
    bindToController: {
      ad: '=',
      form: '=',
      categoryInfo: '=',
    },
    controller(deviceService, $scope, store, config, $timeout, PhotoTransfer) {
      'ngInject';

      const apiBaseUrl = config.get('monolith-python.browserUrl');
      /**
       * на данный момент у частей нет id,
       * но так как мы теперь в качестве view используем реакт
       * нам необходимо эти айдишники иметь
       * @todo убрать этот код, когда бэк начнет класть id внутрь
       */
      if (this.ad.areaParts?.length) {
        this.ad.areaParts = this.ad.areaParts.map(item => ({
          ...item,
          id: item.id ?? uuidv4(),
          priceType: item.priceType || (this.ad.bargainTerms.priceType ?? null),
          paymentPeriod: item.paymentPeriod || (this.ad.bargainTerms.paymentPeriod ?? null),
          /**
           * Для кейса с восстановлением из архива или черновика, поле "floorFrom" приходит в значении undefined,
           * что приводит к тому, что оно не валидируется из-за логики в функции валидации.
           */
          floorFrom: formatFloorFromValue(item.floorFrom),
          floorTo: item.floorTo !== item.floorFrom ? item.floorTo : undefined,
          photos: item.photos?.length
            ? item.photos.map(item => ({
                id: item.id,
                name: item.name,
                size: item.size,
                preview: `${apiBaseUrl}addform/v2/images/${item.id}/`,
                rotateDegree: 0,
              }))
            : [],
        }));

        // Установка полей не консистентна с бэкендом, пофиксить в https://jira.cian.tech/browse/CD-157987
        this.ad.floorNumber = findMax('floorFrom', this.ad.areaParts);
        this.ad.totalArea = findMax('area', this.ad.areaParts);
        this.ad.bargainTerms.price = findMax('price', this.ad.areaParts);
      }

      this.validationHelpers = new ValidationHelpers();

      // Поле передается в шаблон, поэтому не можем от него избавиться
      this.invalidFieldsByID ??= {};

      // Обновляем набор ошибок, чтобы шаблоны увидели изменения в "invalidFieldsByID"
      this.updateInvalidFields = () => {
        this.invalidFieldsByID = this.validationHelpers.errors;
      };

      this.uploadErrors = !this.ad.areaParts?.length
        ? []
        : this.ad.areaParts.map(item => ({ id: item.id, errors: [] }));

      this.isAddButtonShown = () => {
        return Boolean(this.ad.areaParts && this.ad.areaParts.length < MAX_AREA_PARTS_LENGTH);
      };

      this.addArea = () => {
        const isSale = this.categoryInfo.dealType === 'sale';
        const areaPart = isSale ? {} : { paymentPeriod: 'monthly' };
        const newItem = createAreaPartItem(areaPart);

        if (!this.ad.areaParts) {
          if (!isSale) {
            const state = store.getState();
            const isOfferEditing = !selectIsNewOffer(state);
            const product = this.ad.id && {
              id: this.ad.id,
              offerType: 'offer',
              position: 1,
            };
            const siteType = getClientDeviceType();
            trackClickIsAreaParts(isOfferEditing, product, siteType);
          }

          const additionalItem = createAreaPartItem(areaPart);

          this.ad.areaParts = [newItem, additionalItem];

          this.uploadErrors = [
            { id: newItem.id, errors: [] },
            { id: additionalItem.id, errors: [] },
          ];

          return;
        }

        this.ad.areaParts = this.ad.areaParts.concat(newItem);
        this.uploadErrors = [...this.uploadErrors, { id: newItem.id, errors: [] }];
      };

      this.removeArea = idForRemove => {
        this.ad.areaParts = this.ad.areaParts.filter(item => item.id !== idForRemove);
        this.uploadErrors = this.uploadErrors.filter(item => item.id !== idForRemove);

        // Установка полей не консистентна с бэкендом, пофиксить в https://jira.cian.tech/browse/CD-157987
        this.ad.floorNumber = findMax('floorFrom', this.ad.areaParts);
        this.ad.totalArea = findMax('area', this.ad.areaParts);
        this.ad.bargainTerms.price = findMax('price', this.ad.areaParts);

        this.validationHelpers.removeFieldError([idForRemove]);
        this.updateInvalidFields();
      };

      this.onFieldChange = (currentIndex, field, value) => {
        this.ad.areaParts = assocPath([currentIndex, field], value, this.ad.areaParts);

        // Установка полей не консистентна с бэкендом, пофиксить в https://jira.cian.tech/browse/CD-157987
        this.ad.floorNumber = findMax('floorFrom', this.ad.areaParts);

        if (field === 'area') {
          this.ad.totalArea = findMax('area', this.ad.areaParts);
        }

        if (field === 'price') {
          this.ad.bargainTerms.price = (this.ad.bargainTerms.price ?? 0) < value ? value : this.ad.bargainTerms.price;
        }

        if (field === 'priceType') {
          const [priceType, paymentPeriod] = value.split('_');

          this.ad.areaParts = compose(
            assocPath([currentIndex, 'paymentPeriod'], paymentPeriod),
            assocPath([currentIndex, 'priceType'], priceType),
          )(this.ad.areaParts);
        }

        const { id } = this.ad.areaParts[currentIndex];
        const fieldPreviousError = this.validationHelpers.getFieldError([id, field]);

        if (fieldPreviousError) {
          this.validationHelpers.removeFieldError([id]);
          this.updateInvalidFields();
        }

        $scope.$apply();
      };

      this.onReset = () => {
        this.ad.areaParts = undefined;
        this.ad.floorNumber = undefined;
        this.ad.totalArea = undefined;
        this.ad.bargainTerms.price = undefined;

        this.validationHelpers.resetErrors();
        this.updateInvalidFields();
      };

      this.validateField = (fieldIndex, key) => {
        const field = this.ad.areaParts[fieldIndex];
        const value = field[key];

        const error = getValidationMessage(key, value, this.ad, field)?.replace(
          '{slotNumber}',
          `Площадь ${fieldIndex + 1}`,
        );

        const fieldPreviousError = this.validationHelpers.getFieldError([field.id, key]);
        const isFieldChangedFromInvalidToValid = error == null && fieldPreviousError;

        if (isFieldChangedFromInvalidToValid) {
          this.validationHelpers.removeFieldError([field.id, key]);
        }

        if (error != null) {
          this.validationHelpers.setFieldError([field.id, key], error);
        }

        this.updateInvalidFields();
      };

      this.validateAllFields = () => {
        this.ad.areaParts.forEach((item, idx) => {
          const validatableFieldsWithoutUndefinedValues = VALIDATABLE_FIELDS.filter(field => item[field] !== undefined);

          validatableFieldsWithoutUndefinedValues.forEach(field => {
            this.validateField(idx, field);
          });
        });

        this.form.areaParts.$setValidity('required', this.validationHelpers.isFormValid);
      };

      this.onPhotoSelect = index => async photos => {
        const currentPhotos = this.ad.areaParts[index].photos;
        this.uploadErrors = assocPath([index, 'errors'], [], this.uploadErrors);

        const newPhotos = [];
        const newUploadErrors = [];
        let photoCounter = currentPhotos.length;

        Array.from(photos).forEach((photo, i) => {
          if (this.ad.areaParts[index].photos.length > 4) {
            return;
          }

          const newPhoto = {
            name: photo.name,
            size: photo.size,
            rotateDegree: 0,
          };
          newPhotos.push(newPhoto);

          this.ad.areaParts = assocPath([index, 'photos'], [...currentPhotos, ...newPhotos], this.ad.areaParts);

          if (photo.size > 10485760) {
            newUploadErrors.push(`Размер ${photo.name} больше 10Мб`);
            this.uploadErrors = assocPath([index, 'errors'], newUploadErrors, this.uploadErrors);
          } else {
            PhotoTransfer.upload(photo, '/addform/v2/images/')
              .then(response => {
                if (response && response.data && response.data.id) {
                  newPhotos[i] = {
                    ...newPhotos[i],
                    id: response.data.id,
                    preview: `${apiBaseUrl}addform/v2/images/${response.data.id}/`,
                  };

                  this.ad.areaParts = assocPath([index, 'photos'], [...currentPhotos, ...newPhotos], this.ad.areaParts);
                }
              })
              .catch(() => {
                newUploadErrors.push(`Ошибка загрузки ${photo.name}, пожалуйста, повторите позднее`);
                this.uploadErrors = assocPath([index, 'errors'], newUploadErrors, this.uploadErrors);
              })
              .finally(() => {
                photoCounter++;

                if (photoCounter === this.ad.areaParts[index].photos.length) {
                  const filteredPhotos = this.ad.areaParts[index].photos.filter(item => item.id);
                  this.ad.areaParts = assocPath([index, 'photos'], filteredPhotos, this.ad.areaParts);
                }
              });
          }
        });
      };

      this.onPhotoRemove = areaPartIndex => photoId => {
        const newPhotos = this.ad.areaParts[areaPartIndex].photos.filter(photo => photo.id !== photoId);

        this.ad.areaParts = assocPath([areaPartIndex, 'photos'], newPhotos, this.ad.areaParts);
        this.uploadErrors = assocPath([areaPartIndex, 'errors'], [], this.uploadErrors);
      };

      this.onPhotoRotate = areaPartIndex => photoId => {
        const newPhotos = this.ad.areaParts[areaPartIndex].photos.map(photo => {
          return {
            ...photo,
            rotateDegree: photo.id === photoId ? (photo.rotateDegree + 90) % 360 : photo.rotateDegree,
          };
        });

        this.ad.areaParts = assocPath([areaPartIndex, 'photos'], newPhotos, this.ad.areaParts);
        this.uploadErrors = assocPath([areaPartIndex, 'errors'], [], this.uploadErrors);
      };

      $scope.$on('addform.clear', () => {
        this.onReset();
      });

      $scope.$on('addform.submit', () => {
        if (this.ad.areaParts?.length) {
          this.validateAllFields();

          const siteType = getAnalyticsSiteType();
          const maxAreaPart = this.ad.areaParts.reduce((max, curr) => (max.area > curr.area ? max : curr));
          const isSale = this.categoryInfo.dealType === 'sale';

          trackSubmitAreaPartsOffer(this.ad, siteType, maxAreaPart, isSale);
        }
      });

      $scope.$watch(
        () => this.ad.building.floorsCount,
        () => {
          if (!this.ad.areaParts) {
            return;
          }

          this.ad.areaParts.forEach((areaPart, index) => {
            if (!areaPart.floorFrom) {
              return;
            }

            this.validateField(index, 'floorFrom');

            if (areaPart.floorTo !== undefined) {
              this.validateField(index, 'floorTo');
            }
          });
          $timeout(function () {
            $scope.$apply();
          });
        },
      );

      $scope.$watch(
        () => this.categoryInfo.dealType,
        (oldDealType, newDealType) => {
          if (oldDealType && newDealType && oldDealType !== newDealType) {
            this.onReset();
          }
        },
      );
    },
    controllerAs: 'ctrl',
    template,
  })).name;

function createAreaPartItem(areaPart = {}) {
  const {
    price = null,
    area = null,
    floorFrom = null,
    floorTo,
    id = uuidv4(),
    priceType = 'squareMeter',
    paymentPeriod,
    photos = [],
  } = areaPart;

  return { area, price, floorFrom, floorTo, id, priceType, paymentPeriod, photos };
}

function formatFloorFromValue(floorFrom) {
  if (floorFrom === undefined) {
    return null;
  }

  return floorFrom;
}
